// App-wide cross-tab session sync.
//
// Mounted once, at the root, for every page — public and private alike. The `_gcc`
// cookie is shared by every tab on the same origin, but nothing tells an already-open
// tab when it changes: log in on tab A and tab B (sitting on the login page, or on a
// now-stale dashboard) has no way to know until its next navigation. This makes every
// open tab react immediately, in either direction:
//   - someone logs in anywhere  → every other tab reloads and lands wherever its own
//     loaders now say it belongs (dashboard, if it was on a login page; its own
//     dashboard, if the identity changed; unchanged, if not authenticated-relevant)
//   - someone logs out anywhere → every other tab reloads and its own loaders
//     (`getUserObject`/`requireRole`/`requireUser`) find no session and redirect to
//     the correct login page on their own — this file never has to know which login
//     page that is
//
// Deliberately generic across every login/logout path in the app (OAuth, employee
// email/password, customer PIN) — it keys off "did the session identity change",
// not any one auth strategy, so a new login/logout route never needs to remember to
// wire this up itself.
import { useEffect } from "react";
import { useLocation } from "@remix-run/react";

const CHANNEL_NAME = "gcc_session";
const STORAGE_KEY = "gcc_session_marker";
const PENDING_BU_KEY = "gcc_pending_bu_switch";

type SessionEvent =
  | { type: "login" | "logout" }
  | { type: "bu-switch"; bu: string };

/** Post an event to every other tab on `CHANNEL_NAME`. Never delivered back to the sender. */
function postSessionEvent(event: SessionEvent) {
  try {
    const channel = new BroadcastChannel(CHANNEL_NAME);
    channel.postMessage(event);
    channel.close();
  } catch {
    // BroadcastChannel isn't available in every environment (older WebViews) —
    // this tab's own navigation still lands correctly either way; only the
    // *other* tabs miss the instant notice.
  }
}

function broadcastSessionEvent(type: "login" | "logout") {
  postSessionEvent({ type });
}

/**
 * Records the BU this tab just asked to switch to, called from
 * `useBusinessUnitSwitch`'s `onSelect` right before submitting. Read back by
 * `useBuSwitchConfirmation` once the redirect lands, wherever it lands —
 * `sessionStorage` (not a component ref) because the switch action redirects
 * to the employee home screen, not back to whichever route (profile, or the
 * home screen's own drawer) initiated the switch, so the component that
 * called this is often already unmounted by the time it's confirmed.
 */
export function markPendingBuSwitch(bu: string) {
  try {
    sessionStorage.setItem(PENDING_BU_KEY, bu);
  } catch {
    // No sessionStorage — this tab's own switch still works via the normal
    // redirect; only the cross-tab broadcast is skipped.
  }
}

/**
 * Mounted once at the root. On every navigation, checks whether the landed-on
 * page's `$company` segment matches a pending BU switch — confirming it
 * actually took effect (a rejected switch redirects to the *previous* BU's
 * home instead) — and broadcasts it to every other tab.
 */
export function useBuSwitchConfirmation() {
  const { pathname } = useLocation();

  useEffect(() => {
    let pending: string | null = null;
    try {
      pending = sessionStorage.getItem(PENDING_BU_KEY);
    } catch {
      return;
    }
    if (!pending) return;

    if (pathname.split("/")[1] === pending) {
      postSessionEvent({ type: "bu-switch", bu: pending });
    }
    try {
      sessionStorage.removeItem(PENDING_BU_KEY);
    } catch {
      // Nothing to clean up if storage isn't available.
    }
  }, [pathname]);
}

/**
 * Swaps the `$company` segment of an employee-app pathname to `bu`. Returns
 * null for any other path (public pages, shipper/carrier routes) — those
 * aren't scoped to an employee's resource-group BU, so a switch doesn't
 * affect them; that tab's own cookie/loader is already correct for whenever
 * it next navigates into the employee app.
 */
function withBu(pathname: string, bu: string): string | null {
  const segments = pathname.split("/");
  if (segments[2] !== "app" || segments[3] !== "employee") return null;
  segments[1] = bu;
  return segments.join("/");
}

/**
 * Compares `sessionMarker` (an opaque "who's logged in" string, or null when no
 * one is) against what this browser last saw, broadcasts on a real change, and
 * reloads this tab when another one does the same.
 *
 * `sessionMarker` should be stable for one identity and change whenever the
 * identity does — e.g. `${type}:${user_id}` — but not on incidental session
 * changes that aren't a login/logout (a business-unit switch keeps the same
 * user_id, so it correctly does *not* trigger a cross-tab reload here).
 */
export function useSessionBroadcast(sessionMarker: string | null) {
  useEffect(() => {
    const previous = localStorage.getItem(STORAGE_KEY);

    if (sessionMarker) {
      if (sessionMarker !== previous) {
        localStorage.setItem(STORAGE_KEY, sessionMarker);
        broadcastSessionEvent("login");
      }
    } else if (previous) {
      localStorage.removeItem(STORAGE_KEY);
      broadcastSessionEvent("logout");
    }

    let channel: BroadcastChannel | null = null;
    try {
      channel = new BroadcastChannel(CHANNEL_NAME);
      channel.onmessage = (event: MessageEvent<SessionEvent>) => {
        const data = event.data;
        if (data?.type === "login" || data?.type === "logout") {
          window.location.reload();
          return;
        }
        if (data?.type === "bu-switch") {
          const target = withBu(window.location.pathname, data.bu);
          if (target) window.location.href = `${target}${window.location.search}`;
        }
      };
    } catch {
      // No BroadcastChannel support — this tab just won't hear about other tabs;
      // its own next navigation still resolves correctly against the live cookie.
    }

    return () => channel?.close();
  }, [sessionMarker]);
}
